// src/utils/charts/tokenSeries.ts
/**
 * Token Series Utilities
 * 
 * Shared utilities for converting token data into chart series format.
 * Used by MultiTokenChart consumers like ChartSection and CompareTab.
 */

import { CHART_COLORS } from '@/utils/charts/theme';

// ============================================================================
// TYPES
// ============================================================================

export interface TokenConfig {
  symbol: string;
  name?: string | undefined;
  color?: string | undefined;
}

export interface TokenDataPoint {
  timestamp: string;
  [symbol: string]: string | number;
}

export interface SeriesData {
  dates: number[];
  values: number[];
}

export interface MultiTokenSeriesItem {
  name: string;
  values: (number | null)[];
  color?: string | undefined;
}

// ============================================================================
// COLOR ASSIGNMENT
// ============================================================================

/**
 * Consistent color palette for token series (used internally)
 */
const TOKEN_COLORS = CHART_COLORS.series;

// ============================================================================
// DATA EXTRACTION
// ============================================================================

/**
 * Extracts dates and series from API response format
 * Used by ChartSection which receives TimeSeriesApiResponse
 */
export function extractFromApiResponse(
  data: TokenDataPoint[],
  tokens: TokenConfig[]
): { dates: string[]; series: MultiTokenSeriesItem[] } {
  if (!data || data.length === 0 || tokens.length === 0) {
    return { dates: [], series: [] };
  }

  const dates = data.map((point) => {
    const dateStr = new Date(point.timestamp).toISOString().split("T")[0];
    return dateStr || '';
  });

  const series: MultiTokenSeriesItem[] = tokens.map((token, idx) => ({
    name: token.name || token.symbol,
    values: data.map((point) => {
      const val = point[token.symbol];
      return typeof val === 'number' ? val : null;
    }),
    color: token.color || TOKEN_COLORS[idx % TOKEN_COLORS.length],
  }));

  return { dates, series };
}

/**
 * Extracts dates and series from aligned series format
 * Used by CompareTab which uses SeriesData format
 */
export function extractFromAlignedSeries(
  alignedSeries: Record<string, SeriesData>,
  tickers: string[],
  colorMap?: Record<string, string>
): { dates: number[]; series: MultiTokenSeriesItem[] } {
  // Find dates from first available ticker
  let dates: number[] = [];
  for (const t of tickers) {
    const s = alignedSeries[t];
    if (s && s.dates.length > 0) {
      dates = s.dates;
      break;
    }
  }

  if (dates.length === 0) {
    return { dates: [], series: [] };
  }

  const series: MultiTokenSeriesItem[] = [];
  
  tickers.forEach((ticker, idx) => {
    const s = alignedSeries[ticker];
    if (!s) return;
    
    series.push({
      name: ticker,
      values: s.values.map(v => Number.isFinite(v) ? v : null),
      color: colorMap?.[ticker] || TOKEN_COLORS[idx % TOKEN_COLORS.length],
    });
  });

  return { dates, series };
}

// ============================================================================
// VALUE NORMALIZATION
// ============================================================================

/**
 * Normalizes series values to start at a base value (default 100)
 * Useful for comparing tokens with different price scales
 */
export function indexSeriesToBase(
  values: (number | null)[],
  base: number = 100
): (number | null)[] {
  const firstValidIdx = values.findIndex(v => v !== null && Number.isFinite(v) && v !== 0);
  if (firstValidIdx === -1) return values;
  
  const firstValue = values[firstValidIdx] as number;
  return values.map(v => {
    if (v === null || !Number.isFinite(v)) return null;
    return (v / firstValue) * base;
  });
}

/**
 * Normalizes multiple series to 100% at each time point
 */
export function normalizeToPercent(
  series: MultiTokenSeriesItem[]
): MultiTokenSeriesItem[] {
  if (series.length === 0) return series;
  
  const n = series[0]?.values.length || 0;
  const normalized = series.map(s => ({ ...s, values: [...s.values] }));
  
  for (let idx = 0; idx < n; idx++) {
    const total = normalized.reduce((sum, s) => {
      const v = s.values[idx];
      return sum + (typeof v === 'number' && Number.isFinite(v) ? Math.abs(v) : 0);
    }, 0);
    
    if (total > 0) {
      normalized.forEach(s => {
        const v = s.values[idx];
        s.values[idx] = typeof v === 'number' ? (Math.abs(v) / total) * 100 : 0;
      });
    }
  }
  
  return normalized;
}

/**
 * Applies index-to-100 normalization to all series
 */
export function indexAllSeries(
  series: MultiTokenSeriesItem[],
  base: number = 100
): MultiTokenSeriesItem[] {
  return series.map(s => ({
    ...s,
    values: indexSeriesToBase(s.values, base),
  }));
}

// ============================================================================
// METRIC FORMATTING HELPERS
// ============================================================================

/**
 * Determines Y-axis format based on metric ID
 */
export type YAxisFormatType = 'currency' | 'percent' | 'ratio' | 'number';

export function inferYAxisFormat(metricId: string): YAxisFormatType {
  const id = metricId.toLowerCase();
  
  // IMPORTANT: check ratio-like metrics first, since many ratio IDs also contain substrings
  // like "fee"/"mcap" that would otherwise get misclassified as currency.
  if (id.includes('ratio') || id.includes('sharpe') || id.includes('sortino') ||
      id.includes('calmar') || id.includes('omega')) {
    return 'ratio';
  }
  
  // Percent-like metrics (include futures funding rate keys, which often contain "oi")
  if (id.includes('return') || id.includes('change') || id.includes('pct') ||
      id.includes('rate') || id.includes('yield') ||
      id.includes('funding') || id.includes('futures_fr')) {
    return 'percent';
  }
  
  if (id.includes('price') || id.includes('fee') || id.includes('revenue') || 
      id.includes('tvl') || id.includes('mcap') || id.includes('market_cap') ||
      id.includes('volume') || id.includes('oi')) {
    return 'currency';
  }
  
  return 'number';
}

/**
 * Determines if a metric can be indexed to 100
 */
export function canIndexMetric(metricId: string): boolean {
  const id = metricId.toLowerCase();
  
  // Don't index ratio/rate metrics
  if (id.includes('ratio') || id.includes('sharpe') || id.includes('sortino') ||
      id.includes('rate') || id.includes('pct') || id.includes('change')) {
    return false;
  }
  
  // Index price-like metrics
  if (id.includes('price') || id.includes('tvl') || id.includes('mcap') ||
      id.includes('market_cap') || id.includes('volume') || id.includes('oi')) {
    return true;
  }
  
  return false;
}

